/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

/**
 * A stripped m.room.member event which contains the key render-able fields from the event,
 * sent only in simplified sliding sync (not `/v3/sync`).
 * This is very similar to MSC4186Hero from sliding-sync.ts but an internal format with
 * camelCase rather than underscores.
 */
export type Hero = {
  userId: string;
  displayName?: string;
  avatarUrl?: string;
};

/**
 * High level summary information for a room, as returned by `/v3/sync`.
 */
export type IRoomSummary = {
  /**
   * The room heroes: a selected set of members that can be used when summarizing or
   * generating a name for a room. List of user IDs.
   */
  'm.heroes': string[];
  /**
   * The number of joined members in the room.
   */
  'm.joined_member_count'?: number;
  /**
   * The number of invited members in the room.
   */
  'm.invited_member_count'?: number;
};

type IInfo = {
  /** The title of the room (e.g. `m.room.name`) */
  title: string;
  /** The description of the room (e.g. `m.room.topic`) */
  desc?: string;
  /** The number of joined users. */
  numMembers?: number;
  /** The list of aliases for this room. */
  aliases?: string[];
  /** The timestamp for this room. */
  timestamp?: number;
};

/**
 * Construct a new Room Summary. A summary can be used for display on a recent
 * list, without having to load the entire room list into memory.
 * @param roomId - Required. The ID of this room.
 * @param info - Optional. The summary info. Additional keys are supported.
 */
export class RoomSummary {
  public constructor(public readonly roomId: string, public readonly info?: IInfo) {}

  public getTitle(): string {
    return this.info?.title || this.roomId;
  }

  public getDescription(): string {
    return this.info?.desc || '';
  }
}
